import React from 'react';
import { Info, Calendar, Briefcase, CalendarCheck, Calculator, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о расчете дат и полезными сведениями
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-cyan-500" />
        Всё о расчёте дат: особенности, методы, применение
      </h2>
      
      <div className="prose prose-cyan dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-500" />
          Основы расчета дат
        </h3>
        <p>
          <strong>Расчет дат</strong> — это определение количества дней между двумя датами, либо нахождение даты, отстоящей от заданной на определенное количество дней. Такие расчеты играют важную роль в планировании проектов, определении сроков, расчете стажа работы и во многих других областях.
        </p>
        <p>
          В России расчет дат имеет свои особенности, связанные с учетом официальных праздников, переносом рабочих дней и другими факторами, регламентируемыми Трудовым кодексом РФ.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-cyan-500" />
          Методы расчета дат
        </h3>
        <ul>
          <li><strong>Разница между датами:</strong> Определение точного количества дней, недель, месяцев и лет между двумя датами. При этом важно учитывать, включается ли конечная дата в подсчет.</li>
          <li><strong>Прибавление дней:</strong> Расчет даты, которая наступит через определенное количество дней после исходной даты. Особенно полезно при планировании сроков.</li>
          <li><strong>Вычитание дней:</strong> Определение даты, которая была за определенное количество дней до исходной даты. Применяется для ретроспективного анализа.</li>
          <li><strong>Расчет рабочих дней:</strong> Подсчет только рабочих дней (обычно понедельник-пятница) без учета выходных и праздников. Критически важен для бизнес-планирования.</li>
        </ul>

        <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-100 dark:border-cyan-800 my-6">
          <h4 className="text-cyan-700 dark:text-cyan-300 font-medium">Важно знать для расчетов в России</h4>
          <p className="text-cyan-600 dark:text-cyan-400 text-sm mb-0">
            В России при расчете рабочих дней необходимо учитывать не только выходные (суббота и воскресенье), но и государственные праздники. Кроме того, следует обращать внимание на переносы выходных дней в соответствии с Постановлениями Правительства РФ.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-cyan-500" />
          Применение в профессиональной сфере
        </h3>
        <p>
          Точный расчет дат необходим во многих профессиональных областях:
        </p>
        <ul>
          <li>
            <strong>Управление проектами:</strong> Планирование сроков выполнения задач, расчет длительности проекта, определение критического пути.
          </li>
          <li>
            <strong>Трудовое право:</strong> Расчет трудового стажа, отпускных дней, больничных листов, компенсаций при увольнении.
          </li>
          <li>
            <strong>Бухгалтерия и финансы:</strong> Определение сроков уплаты налогов, расчет периодов начисления процентов, подготовка отчетности.
          </li>
          <li>
            <strong>Логистика:</strong> Планирование маршрутов, расчет времени доставки, определение оптимальных графиков.
          </li>
          <li>
            <strong>Юриспруденция:</strong> Определение процессуальных сроков, исковой давности, дат исполнения обязательств по договорам.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-cyan-500" />
          Государственные праздники в России
        </h3>
        <p>
          При расчете рабочих дней в России необходимо учитывать следующие нерабочие праздничные дни согласно статье 112 Трудового кодекса РФ:
        </p>
        <ul>
          <li>1, 2, 3, 4, 5, 6 и 8 января — Новогодние каникулы</li>
          <li>7 января — Рождество Христово</li>
          <li>23 февраля — День защитника Отечества</li>
          <li>8 марта — Международный женский день</li>
          <li>1 мая — Праздник Весны и Труда</li>
          <li>9 мая — День Победы</li>
          <li>12 июня — День России</li>
          <li>4 ноября — День народного единства</li>
        </ul>
        <p>
          Важно помнить, что если праздничный день приходится на выходной, то выходной день переносится на следующий после праздничного рабочий день. Правительство РФ может переносить выходные дни в целях рационального использования работниками выходных и нерабочих праздничных дней.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при расчете дат
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>При подсчете разницы между датами важно определить, включается ли конечный день в подсчет</li>
            <li>В високосные годы в феврале 29 дней, что может влиять на точность расчетов</li>
            <li>Региональные праздники могут отличаться от федеральных</li>
            <li>При международных расчетах учитывайте различия в календарях и выходных днях разных стран</li>
            <li>Для точного расчета рабочих дней используйте актуальный производственный календарь</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-cyan-500" />
          Практические советы
        </h3>
        <ul>
          <li><strong>Используйте актуальный производственный календарь:</strong> Правительство РФ ежегодно утверждает переносы выходных дней, что влияет на расчет рабочих дней.</li>
          <li><strong>Сохраняйте часто используемые расчеты:</strong> Это позволит быстро возвращаться к ним без необходимости повторного ввода данных.</li>
          <li><strong>Учитывайте временные зоны:</strong> При международных расчетах помните о разнице во времени между регионами.</li>
          <li><strong>Проверяйте данные:</strong> Даже небольшая ошибка в дате может привести к существенным расхождениям в результатах расчетов.</li>
          <li><strong>Экспортируйте результаты:</strong> Сохраняйте результаты расчетов в формате CSV для дальнейшего использования в других программах.</li>
        </ul>

        <h3>Формулы для расчета дат</h3>
        <p>
          При программной реализации расчета дат используются различные алгоритмы:
        </p>
        <ol>
          <li><strong>Расчет дней между датами:</strong> Разница в миллисекундах, деленная на количество миллисекунд в дне (1000 * 60 * 60 * 24).</li>
          <li><strong>Расчет месяцев между датами:</strong> Разница в годах, умноженная на 12, плюс разница в месяцах с учетом дней.</li>
          <li><strong>Расчет рабочих дней:</strong> Подсчет дней с понедельника по пятницу, исключая праздники.</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш календарный счетчик дней поможет вам выполнить все эти расчеты быстро и точно с учетом российских особенностей!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;